import { NiceService } from "../../../services/nice.service";
import { IInteractionStrategy } from "../../../../core-ui/models/strategies";
import {
  ConversationChannelEnum,
  ConversationTypeEnum,
  EndpointTypeEnum,
  GenericErrorHandler,
  IConversationEventHandlers,
  IInteraction,
  IInteractionDetails,
  IInteractionMessageOptions,
  InteractionEventClient,
  ProtectedTicketService,
} from "../../../../core-ui";
import { NiceInteraction } from "../../NiceInteraction";
import { NiceInteractionDetails } from "../../NiceInteractionDetails";
import {
  PARAM_CALL_TYPE,
  WIDGET_COBROWSE_REQUEST_PREFIX,
} from "../../../../app/app.enums";

export class NiceInteractionStrategy implements IInteractionStrategy {
  private _interaction: IInteraction = null;
  private _eventClient: InteractionEventClient;

  constructor(
    private ticketService: ProtectedTicketService,
    private errorHandler: GenericErrorHandler,
    private niceService: NiceService
  ) {
    this._eventClient = new InteractionEventClient();
    this.niceService.registerConversationEventHandlers(
      this._eventClient.getHandlers()
    );
  }

  isActiveInteractionAvailable(): boolean {
    return !!this._interaction;
  }

  getActiveInteraction(): IInteraction {
    return this._interaction;
  }

  setActiveInteraction(interaction: IInteraction) {
    this._interaction = interaction;
  }

  clearActiveInteraction() {
    if (this._interaction) {
      this.niceService.removeContact(this._interaction.getId());
      this._interaction = null;
    }
  }

  async discoverActiveInteraction() {
    try {
      const interaction = await this.niceService.discoverActiveConversation();

      if (interaction)
        this.setActiveInteraction(new NiceInteraction(interaction));

      return this._interaction;
    } catch (ex) {
      return null;
    }
  }

  async retrieveInteractionData(id: string, channel?: ConversationChannelEnum) {
    const info = await this.niceService.retrieveInteractionData(id);
    return info ? new NiceInteraction(info) : null;
  }

  prepareInteractionScheduleRequest() {
    return null;
  }

  scheduleInteraction() {
    return null;
  }

  createEmailInteraction() {
    return null;
  }

  createSMSInteraction() {
    return null;
  }

  invite(
    toConversationType: ConversationTypeEnum,
    interaction: NiceInteraction,
    ticket: string,
    options?: IInteractionMessageOptions
  ) {
    let message: string;
    switch (toConversationType) {
      case ConversationTypeEnum.cobrowse:
        message = this.getInvitation(toConversationType, ticket, options);
        break;
      case ConversationTypeEnum.videoCall:
      case ConversationTypeEnum.voiceCall:
      case ConversationTypeEnum.chat:
        let link: URL | string = "";
        const customerLink = this.getInvitation(toConversationType, ticket);

        try {
          link = new URL(customerLink);
          link.searchParams.set(PARAM_CALL_TYPE, interaction.getType());
        } catch (ex) {
          this.errorHandler.handleError(ex.message + " -> " + customerLink);
          link = customerLink.includes("?")
            ? `${link}&${PARAM_CALL_TYPE}=${interaction.getType()}`
            : `${link}?${PARAM_CALL_TYPE}=${interaction.getType()}`;
        }

        message = link.toString();
        break;
    }

    return this.postMessageToInteraction(interaction, message, options);
  }

  getInvitation(
    forConversationType: ConversationTypeEnum,
    ticket: string,
    options?: IInteractionMessageOptions
  ): string {
    let message;
    switch (forConversationType) {
      case ConversationTypeEnum.cobrowse:
        message = [
          WIDGET_COBROWSE_REQUEST_PREFIX,
          options?.requestType || "view",
          ticket,
        ].join(":");
        break;
      case ConversationTypeEnum.videoCall:
      case ConversationTypeEnum.voiceCall:
      case ConversationTypeEnum.chat:
        message = this.ticketService.getTicketWithDomain(ticket);
        break;
    }
    return message;
  }

  async postMessageToInteraction(
    interaction: NiceInteraction,
    message: string,
    options: IInteractionMessageOptions
  ) {
    return this.niceService.postOutboundMessage(interaction.getId(), message);
  }

  getChatTranscript() {
    return null;
  }

  async getInteractionDetails(caseId: string): Promise<IInteractionDetails> {
    const contact = await this.niceService.retrieveContact(caseId);
    return contact ? new NiceInteractionDetails(contact) : null;
  }

  get eventClient(): InteractionEventClient {
    return this._eventClient;
  }
}
